import moment from 'moment'
import Vue from 'vue'
import store from '../store/index.js'
import config from '@/config/index.js'
import { CLICK_ID_PARAMS } from './trafficSource.js'

const SITE = 'WEB_GUI'
const COLLECT_URL = `${config.TRACKING_BASE_API}collect`
const KEY_VISITOR = 'gp_vid'
const KEY_SESSION = 'gp_sid'
const KEY_LAST_ACTIVITY = 'gp_last'
const KEY_ATTRIBUTION = 'gp_attr'
const VISITOR_PARAM = 'gvid'
const IDLE_MS = 30 * 60 * 1000
const FLUSH_INTERVAL_MS = 5000
const FLUSH_QUEUE_SIZE = 10
const MAX_EVENTS_PER_REQUEST = 50
const VISITOR_COOKIE_MAX_AGE = 60 * 60 * 24 * 730
const DATE_FORMAT = 'YYYY-MM-DD'
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const ATTRIBUTION_PARAMS = [
  'gtl', 'agent_id', 'ref_affiliate', 'campaign_id', 'guest_journey_id',
  'utm_source', 'utm_medium', 'utm_campaign',
  ...CLICK_ID_PARAMS,
]

const queue = []
// Fallback when localStorage is blocked (private mode), so ids stay stable for this tab.
const memoryStorage = {}
let landingContext = null
let currentPageType = null
let currentCommunityId = null
let isCommunityPage = false

const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || memoryStorage[key] || null
  } catch (error) {
    return memoryStorage[key] || null
  }
}

const writeStorage = (key, value) => {
  memoryStorage[key] = value
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    return
  }
}

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? match[1] : null
}

const createUUID = () => Vue.prototype.$createUUID()

const getQueryParams = () => new URLSearchParams(window.location.search)

const getVisitorIdFromUrl = () => {
  const visitorId = getQueryParams().get(VISITOR_PARAM)
  return UUID_PATTERN.test(visitorId || '') ? visitorId : null
}

const buildContext = () => ({
  landing_url: window.location.href,
  referrer: document.referrer || null,
})

const getAttributionSignature = () => {
  const params = getQueryParams()
  return ATTRIBUTION_PARAMS.map(key => params.get(key) || '').join('|')
}

const hasAttribution = signature => signature.replace(/\|/g, '') !== ''

const isSessionExpired = (attributionSignature) => {
  const lastActivity = Number(readStorage(KEY_LAST_ACTIVITY) || 0)
  const isIdle = Date.now() - lastActivity > IDLE_MS
  const isNewSource = hasAttribution(attributionSignature)
    && attributionSignature !== readStorage(KEY_ATTRIBUTION)

  return !readStorage(KEY_SESSION) || isIdle || isNewSource
}

const startSession = () => {
  writeStorage(KEY_SESSION, createUUID())
  landingContext = buildContext()
}

/**
 * Returns the browser visitor id, creating and persisting it (localStorage + cookie) on first use.
 * @returns {string} uuid v4
 */
export const ensureVisitorId = () => {
  const visitorId = readStorage(KEY_VISITOR) || readCookie(KEY_VISITOR) || getVisitorIdFromUrl() || createUUID()
  writeStorage(KEY_VISITOR, visitorId)
  document.cookie = `${KEY_VISITOR}=${visitorId}; max-age=${VISITOR_COOKIE_MAX_AGE}; path=/; SameSite=Lax`
  return visitorId
}

/**
 * Returns the visit id, rotating it after 30 idle minutes or when the URL brings a new traffic source.
 * @returns {string} uuid v4
 */
export const ensureSessionId = () => {
  const attributionSignature = getAttributionSignature()
  if (isSessionExpired(attributionSignature)) startSession()
  if (hasAttribution(attributionSignature)) writeStorage(KEY_ATTRIBUTION, attributionSignature)
  writeStorage(KEY_LAST_ACTIVITY, String(Date.now()))
  return readStorage(KEY_SESSION)
}

const formatDate = date => moment(date).format(DATE_FORMAT)

// The landing page_view can fire before the merchant loads; hotel is the default log format.
const shouldAttachHotelProps = () => {
  const merchantType = (store.getters.getMerchant || {}).merchant_type
  return !merchantType || merchantType === 'hotel'
}

const getHotelProps = () => {
  const { date, promo_code: promoCode } = store.getters.getBeFilter
  const hotelProps = {
    check_in: formatDate(date.check_in),
    check_out: formatDate(date.check_out),
  }
  if (promoCode) hotelProps.promo_code = promoCode
  return hotelProps
}

const getDefaultProps = () => (shouldAttachHotelProps() ? getHotelProps() : {})

export const setPageType = (pageType) => {
  currentPageType = pageType || null
}

export const setCommunityId = (communityId) => {
  currentCommunityId = communityId || null
}

/**
 * Queues a visitor event for the Tracking Collect API.
 * @param {string} name event name matching ^[a-z_]{1,40}$
 * @param {Object} props event props; hotel check_in, check_out and promo_code are added automatically
 * @param {Object} options { pageType } overrides the active route page type for this event
 * @returns {void}
 */
export const track = (name, props = {}, options = {}) => {
  ensureSessionId()
  queue.push({
    event_id: createUUID(),
    name,
    ts: new Date().toISOString(),
    page_url: window.location.href,
    page_title: document.title,
    page_type: options.pageType || currentPageType || undefined,
    props: { ...getDefaultProps(), ...props },
  })
  if (queue.length >= FLUSH_QUEUE_SIZE) flushEvents()
}

/**
 * Queues a room search event with the guest count of the active booking filter.
 * @returns {void}
 */
export const trackRoomSearch = () => {
  const { adult, child } = store.getters.getBeFilter.guest || {}
  track('search', { adult, child })
}

const getMerchantId = () => {
  if (isCommunityPage) return null
  return (store.getters.getMerchant || {}).id || null
}

const getCommunityId = () => {
  if (isCommunityPage) return currentCommunityId
  const { community } = store.getters.getMerchant || {}
  return (community && community.id) || null
}

const hasTenant = () => !!(getMerchantId() || getCommunityId())

const buildHeaders = () => {
  const headers = { 'Content-Type': 'application/json' }
  const { access } = store.getters.token || {}
  if (access) headers.Authorization = `Bearer ${access}`
  return headers
}

const buildPayload = events => ({
  visitor_id: ensureVisitorId(),
  session_id: ensureSessionId(),
  site: SITE,
  merchant_id: getMerchantId(),
  community_id: getCommunityId(),
  context: landingContext || buildContext(),
  events,
})

/**
 * Sends queued events (max 50 per request). Failures are ignored so tracking never blocks the guest.
 * @returns {void}
 */
export const flushEvents = () => {
  if (!queue.length || !hasTenant()) return

  // fetch keepalive instead of sendBeacon: sendBeacon cannot send the Authorization header.
  fetch(COLLECT_URL, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(buildPayload(queue.splice(0, MAX_EVENTS_PER_REQUEST))),
    keepalive: true,
  }).catch(() => {})
}

/**
 * Flushes pending events and returns the ids that link an order to this visit.
 * @returns {Object} { tracking_visitor_id, tracking_session_id } for the tx-payment body
 */
export const resolveOrderTrackingFields = () => {
  flushEvents()
  return {
    tracking_visitor_id: ensureVisitorId(),
    tracking_session_id: ensureSessionId(),
  }
}

/**
 * Adds ?gvid=<visitor_id> so the group booking app recognises the same visitor across domains.
 * @param {string} url target url
 * @returns {string} url with the visitor param
 */
export const appendVisitorParam = (url) => {
  const separator = url.indexOf('?') === -1 ? '?' : '&'
  return `${url}${separator}${VISITOR_PARAM}=${ensureVisitorId()}`
}

// Query-only navigations (auth popup, filters, stripping ?agent_id) stay on the same page.
const isSamePageNavigation = (to, from) => from.matched.length > 0 && to.path === from.path

const handleRouteChange = (to, from) => {
  if (isSamePageNavigation(to, from)) return

  const meta = to.meta || {}
  setPageType(meta.pageType)
  isCommunityPage = meta.module_type === 'community'
  if (meta.trackPageViewInComponent) return

  track('page_view', { trx_id: to.params.tx_order_id })
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') flushEvents()
}

/**
 * Starts visitor tracking: pins the landing context, sends page_view per route and flushes periodically.
 * @param {Object} router vue-router instance
 * @returns {void}
 */
export const initTracker = (router) => {
  ensureVisitorId()
  ensureSessionId()
  router.afterEach(handleRouteChange)
  setInterval(flushEvents, FLUSH_INTERVAL_MS)
  document.addEventListener('visibilitychange', handleVisibilityChange)
}



// WEBPACK FOOTER //
// ./src/utils/tracker.js